import type React from "react";

import { ChevronRight } from "lucide-react";

import { formatCount, formatPercentage } from "../utils/dashboardUtils";

import type { ExperimentData } from "../types/dashboard.types";

interface DashboardLiveExperimentRowProps {
  experiment: ExperimentData;
  onOpenExperiment: () => void;
  onViewInsights: () => void;
}

const getExperimentStatusLabel = (status: ExperimentData["status"]) => {
  if (status === "active") {
    return "Live";
  }

  return `${status.charAt(0).toUpperCase()}${status.slice(1)}`;
};

export const DashboardLiveExperimentRow: React.FC<
  DashboardLiveExperimentRowProps
> = ({ experiment, onOpenExperiment, onViewInsights }) => {
  const isCompletedExperiment = experiment.status === "completed";
  const completionRate = experiment.metrics.completionRate ?? 0;

  return (
    <div
      role="button"
      tabIndex={0}
      className="innovation-dashboard__live-row"
      onClick={onOpenExperiment}
      onKeyDown={(event) => {
        if (event.currentTarget !== event.target) {
          return;
        }

        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpenExperiment();
        }
      }}
    >
      <div className="innovation-dashboard__live-content">
        <div className="innovation-dashboard__live-main">
          <div className="innovation-dashboard__live-title-row">
            <span className="innovation-dashboard__live-title">
              {experiment.name}
            </span>
            <span
              className={`innovation-dashboard__status-pill innovation-dashboard__status-pill--${experiment.status} innovation-dashboard__live-status-pill`}
            >
              {getExperimentStatusLabel(experiment.status)}
            </span>
          </div>
          <div className="innovation-dashboard__progress-track">
            <div
              className="innovation-dashboard__progress-bar"
              style={{ width: `${completionRate}%` }}
            />
          </div>
        </div>

        <div className="innovation-dashboard__live-metrics">
          <div className="innovation-dashboard__live-metric">
            <strong>{formatCount(experiment.metrics.participants)}</strong>
            <span>Participants</span>
          </div>
          <div className="innovation-dashboard__live-metric">
            <strong>
              {formatPercentage(experiment.metrics.completionRate)}
            </strong>
            <span>Completion</span>
          </div>
          <div className="innovation-dashboard__live-metric">
            <strong>{formatCount(experiment.metrics.events24h)}</strong>
            <span>24h Events</span>
          </div>
          {isCompletedExperiment ? (
            <button
              type="button"
              className="innovation-dashboard__live-action"
              onClick={(event) => {
                event.stopPropagation();
                onViewInsights();
              }}
            >
              <strong>View Insights</strong>
            </button>
          ) : (
            <span
              className="innovation-dashboard__live-action-placeholder"
              aria-hidden="true"
            />
          )}
          <ChevronRight
            className="innovation-dashboard__live-arrow"
            size={16}
          />
        </div>
      </div>
    </div>
  );
};
